import { useState } from "react";
import { Share2, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import MobileCaptureDialog from "./MobileCaptureDialog";
import { getUserId } from "../lib/utm";

/**
 * ShareButton — renders a share affordance beneath a product/lender card.
 *
 * Unified flow (2026-08-07 — banking-required mode reverted per user):
 *  1. Click → if sharer has no HITCH-USER-* identity, open an OPTIONAL
 *     mobile capture (sharer can skip and share anonymously).
 *  2. Build share URL /apply/<productId>?ref=<sharer_uid_or_empty> and
 *     either navigator.share or copy to clipboard.
 *
 * Mobile capture is REQUIRED only on the RECIPIENT side (see
 * SharedApplyLanding). Sharers never lose their entry to sharing.
 */
export default function ShareButton({ product, variant = "default" }) {
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [askMobile, setAskMobile] = useState(false);

  const buildLink = (uid) => {
    const base =
      typeof window !== "undefined" && window.location
        ? `${window.location.protocol}//${window.location.host}`
        : "";
    const q = uid ? `?ref=${encodeURIComponent(uid)}` : "";
    return `${base}/apply/${product.id}${q}`;
  };

  const doShare = async (uid) => {
    const link = buildLink(uid);
    const text = `Check out ${product.provider || product.title} on Offer Express — apply in 30 seconds:`;
    setBusy(true);
    try {
      if (typeof navigator !== "undefined" && navigator.share) {
        try {
          await navigator.share({ title: product.title, text, url: link });
          toast.success("Shared");
          return;
        } catch (err) {
          // Ignore user-cancelled AbortError; fall through to clipboard.
          if (err?.name !== "AbortError") throw err;
        }
      }
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(`${text} ${link}`);
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
        toast.success("Link copied — send it to a friend");
      } else {
        // Prehistoric fallback for very old browsers.
        window.prompt("Copy this link:", link);
      }
    } catch {
      toast.error("Could not share. Try copying the URL from address bar.");
    } finally {
      setBusy(false);
    }
  };

  const handleClick = async () => {
    const uid = getUserId();
    if (uid) {
      // Already identified — share immediately with attribution.
      await doShare(uid);
    } else {
      // Ask sharer for mobile (optional). They can skip → anonymous share.
      setAskMobile(true);
    }
  };

  const compact = variant === "compact";
  const base =
    "inline-flex items-center justify-center gap-1.5 rounded-lg border transition-colors font-medium";
  const styles = compact
    ? "h-9 px-3 text-xs border-white/20 bg-white/5 text-white hover:bg-white/10"
    : "w-full h-10 text-xs border-black/10 bg-white text-black hover:bg-neutral-50";

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        disabled={busy}
        data-testid={`share-btn-${product.id}`}
        className={`${base} ${styles}`}
      >
        {busy ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : copied ? (
          <Check className="w-3.5 h-3.5" />
        ) : (
          <Share2 className="w-3.5 h-3.5" />
        )}
        {copied ? "Copied" : "Share with a friend"}
      </button>

      <MobileCaptureDialog
        open={askMobile}
        onOpenChange={setAskMobile}
        product={product}
        optional
        title="Get your referral link"
        description="Enter your mobile to earn cashback when someone applies via your link. You can skip to share anonymously."
        ctaLabel="Save & get share link"
        onCaptured={async (data) => {
          setAskMobile(false);
          await doShare(data?.hitch_user_id);
        }}
        onSkip={async () => {
          setAskMobile(false);
          await doShare(null);
        }}
      />
    </>
  );
}
